// Printer role config + QZ actions for the register's Printer Settings screen (the browser equivalent
// of Android's Printers settings: pick a Payment Receipt printer + a Packing Slip printer, per lane).
// Config is stored PER LANE in localStorage. The cash drawer is wired to the Payment Receipt printer,
// so the "drawer" role IS the receipt printer — it writes the same keys cashDrawer.js already reads
// (qz_drawer_*), which means the drawer needs no change to honour a selection made here.

import { loadQzDefault, qzConnect, DEFAULT_KICK_HEX } from "./cashDrawer";
import { buildReceiptLines, buildTestLines, renderLinesToImage, paperWidthDots } from "./receiptImage";

const KEY = { receipt: "qz_drawer_", packing: "qz_packing_" }; // receipt role === drawer printer

function ls(k) {
  try {
    return typeof localStorage !== "undefined" ? localStorage.getItem(k) : null;
  } catch (e) {
    return null;
  }
}
function setLs(k, v) {
  try {
    localStorage.setItem(k, v);
  } catch (e) {
    /* ignore */
  }
}
function rmLs(k) {
  try {
    localStorage.removeItem(k);
  } catch (e) {
    /* ignore */
  }
}

// role: "receipt" | "packing" → { mode: "installed"|"network", name, host, port }
export function loadPrinterRole(role) {
  const p = KEY[role] || KEY.receipt;
  const host = (ls(p + "host") || "").trim();
  const name = (ls(p + "printer") || "").trim();
  const port = Number(ls(p + "port")) || 9100;
  if (host) return { mode: "network", name: "", host, port };
  return { mode: "installed", name, host: "", port: 9100 };
}

export function savePrinterRole(role, cfg) {
  const p = KEY[role] || KEY.receipt;
  if (cfg.mode === "network") {
    setLs(p + "host", String(cfg.host || "").trim());
    setLs(p + "port", String(Number(cfg.port) || 9100));
    rmLs(p + "printer");
    rememberNetworkPrinter(cfg.host, cfg.port);
  } else {
    setLs(p + "printer", String(cfg.name || "").trim());
    rmLs(p + "host");
    rmLs(p + "port");
  }
}

// Remembered network printers (host:port) — the browser can't LAN-scan like Android's Star SDK, so
// once an IP is entered/tested it's kept here and shown in the picker on this lane, so you pick it
// instead of retyping. Most-recent first, capped at 10.
const KNOWN = "qz_known_printers";
export function getKnownNetworkPrinters() {
  try {
    const a = JSON.parse(ls(KNOWN) || "[]");
    return Array.isArray(a) ? a.filter((x) => typeof x === "string" && x) : [];
  } catch (e) {
    return [];
  }
}
export function rememberNetworkPrinter(host, port) {
  const h = String(host || "").trim();
  if (!h) return;
  const entry = h + ":" + (Number(port) || 9100);
  const cur = getKnownNetworkPrinters();
  if (!cur.includes(entry)) setLs(KNOWN, JSON.stringify([entry, ...cur].slice(0, 10)));
}

// A config points at a printer if it has a name (installed) or a host (network).
export function hasTarget(cfg) {
  return !!(cfg && (cfg.mode === "network" ? String(cfg.host || "").trim() : String(cfg.name || "").trim()));
}

function targetConfig(qz, cfg) {
  return cfg.mode === "network" && cfg.host
    ? qz.configs.create({ host: String(cfg.host).trim(), port: Number(cfg.port) || 9100 }, { encoding: "ISO-8859-1" })
    : qz.configs.create(String(cfg.name).trim(), { encoding: "ISO-8859-1" });
}

// List printers QZ can see (Windows-installed only — a network printer not installed in Windows won't
// appear; enter its IP instead). Returns { ok, printers[] } and never throws.
export async function listPrinters() {
  const qz = await loadQzDefault();
  if (!qz) return { ok: false, reason: "no-bridge", printers: [] };
  try {
    await qzConnect(qz);
    const found = await qz.printers.find();
    const printers = Array.isArray(found) ? found : found ? [found] : [];
    return { ok: true, printers };
  } catch (e) {
    return { ok: false, reason: "error", error: e && e.message ? e.message : String(e), printers: [] };
  }
}

// Fire the cash-drawer kick at an explicit target (for the "Open drawer" test button). Never throws.
export async function openDrawerOn(cfg) {
  if (!hasTarget(cfg)) return { ok: false, reason: "no-target" };
  const qz = await loadQzDefault();
  if (!qz) return { ok: false, reason: "no-bridge" };
  try {
    await qzConnect(qz);
    await qz.print(targetConfig(qz, cfg), [{ type: "raw", format: "hex", data: DEFAULT_KICK_HEX }]);
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: "error", error: e && e.message ? e.message : String(e) };
  }
}

// "Test print" — render a bitmap slip (like Android) and print it as an IMAGE.
export async function testPrintOn(cfg) {
  const who = cfg && (cfg.mode === "network" ? cfg.host : cfg.name);
  const dataUrl = renderLinesToImage(buildTestLines(who), paperWidthDots(getPaperWidthMm()));
  return printImageOn(cfg, dataUrl, { paperWidthMm: getPaperWidthMm() });
}

// Receipt paper width (mm of PRINTABLE area): 80mm paper → 72mm, 58mm paper → 48mm. Per lane.
const PW = "qz_paper_width_mm";
export function getPaperWidthMm() {
  const v = Number(ls(PW));
  return v > 0 ? v : 72;
}
export function setPaperWidthMm(mm) {
  setLs(PW, String(Number(mm) || 72));
}

// Print a pre-rendered receipt BITMAP (base64/data-URL PNG) as an IMAGE — the same approach Android
// uses (actionPrintImage). Deterministic on thermal, unlike QZ's flaky HTML renderer. Never throws.
export async function printImageOn(cfg, dataUrl, opts = {}) {
  if (!hasTarget(cfg)) return { ok: false, reason: "no-target" };
  const qz = await loadQzDefault();
  if (!qz) return { ok: false, reason: "no-bridge" };
  try {
    await qzConnect(qz);
    const pw = Number(opts.paperWidthMm) || 72;
    const dst =
      cfg.mode === "network" && cfg.host
        ? { host: String(cfg.host).trim(), port: Number(cfg.port) || 9100 }
        : String(cfg.name).trim();
    const config = qz.configs.create(dst, {
      units: "mm",
      size: { width: pw }, // height omitted → auto (image height)
      density: "203",
      margins: { top: 0, right: 0, bottom: 0, left: 0 },
      colorType: "blackwhite", // 1-bit mono; QZ re-thresholds after scaling → crisp black/white
      scaleContent: true, // fit the bitmap to the paper width (false overflows: canvas PNG has no DPI tag)
      interpolation: "nearest-neighbor", // keep 1-bit during scaling — no gray blending → no fade
      rasterize: true,
    });
    const b64 = String(dataUrl).replace(/^data:image\/\w+;base64,/, "");
    await qz.print(config, [{ type: "pixel", format: "image", flavor: "base64", data: b64 }]);
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: "error", error: e && e.message ? e.message : String(e) };
  }
}

export function hasReceiptPrinter() {
  return hasTarget(loadPrinterRole("receipt"));
}

// Render the finished-order receipt MODEL to a bitmap and print it as an image on the configured
// Payment Receipt printer. { ok:false, reason:"no-printer" } if none set → caller falls back.
export async function printReceipt(model) {
  const cfg = loadPrinterRole("receipt");
  if (!hasTarget(cfg)) return { ok: false, reason: "no-printer" };
  const dataUrl = renderLinesToImage(buildReceiptLines(model || {}), paperWidthDots(getPaperWidthMm()));
  return printImageOn(cfg, dataUrl, { paperWidthMm: getPaperWidthMm() });
}

// DevTools: open the EXACT bitmap we send to the printer in a new tab, so we can see the render
// quality without a physical print. Run `__qzPreview()` in the register console (screenshot it).
if (typeof window !== "undefined") {
  window.__qzPreview = () => {
    const url = renderLinesToImage(buildTestLines("Star TSP143IIILAN Cutter"), paperWidthDots(getPaperWidthMm()));
    try {
      const w = window.open("");
      if (w && w.document && w.document.body) {
        w.document.body.style.background = "#888";
        w.document.body.style.margin = "0";
        w.document.body.style.padding = "20px";
        const img = w.document.createElement("img");
        img.src = url; // data: URL from our own canvas (no external/user input)
        img.style.background = "#fff";
        img.style.imageRendering = "pixelated";
        w.document.body.appendChild(img);
      }
    } catch (e) {
      /* popup blocked */
    }
    return "preview opened (dots=" + paperWidthDots(getPaperWidthMm()) + ")";
  };
}
